/**
 * Checkout Page
 * Stripe SetupIntent checkout flow — two-phase:
 *   Phase 1 (address): POST /subscribe → { subscriptionId, setupIntentClientSecret, customerId }
 *   Phase 2 (card):    stripe.confirmSetup() → POST /subscribe/confirm → success page (awaits activation)
 *
 * With a card already on file the page leads with one action instead (BR-112):
 *   POST /subscribe { useSavedCard: true } → savedCard.status
 *     'succeeded'       → POST /subscribe/confirm → success page
 *     'requires_action' → stripe.handleNextAction() (3-D Secure on this screen) → confirm → success page
 * A 409 (no usable card or billing address), a failed or abandoned authentication, or "Use a different
 * card" shows the card form above, with the plan and units kept.
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import type { DefaultPaymentMethod } from '@resida/api-types/billing';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ApiClientError } from '../lib/api.core';
import {
  useBillingBundles,
  useBillingSubscription,
  useBillingUsage,
  useSubscribeMutation,
  useConfirmSubscriptionMutation,
} from '../hooks/useBilling';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { CardSetupFlow, PaymentAlert } from '../components/billing/CardSetupFlow';
import { ChargeCardLine } from '../components/billing/ChargeDialogParts';
import { useFreshSession } from '../hooks/useFreshSession';
import { getStripe } from '../lib/stripe';
import { formatCardLabel, formatUsd, type BundleTemplate, type BillingAddress } from '../api/billing';

/** Shown above the card form after the saved card could not be used, so the landlord knows why. */
const FALLBACK_NOTES = {
  PAYMENT_METHOD_REQUIRED: "We couldn't use your saved card. Enter a card below to continue with the same plan.",
  BILLING_ADDRESS_REQUIRED: 'We need your billing address to calculate tax. Enter it below to continue with the same plan.',
  AUTHENTICATION_FAILED: "Your bank didn't verify the saved card. Enter a card below to continue with the same plan.",
  AUTHENTICATION_UNAVAILABLE: "We couldn't start your bank's verification. Enter a card below to continue with the same plan.",
} as const;
type FallbackNote = (typeof FALLBACK_NOTES)[keyof typeof FALLBACK_NOTES];

/** The saved card could not complete the subscribe; the card form takes over with `note` explaining why. */
class SavedCardFallback extends Error {
  constructor(readonly note: FallbackNote) {
    super(note);
  }
}

// =============================================================================
// PLAN SUMMARY (shown above each CardSetupFlow phase and on the saved-card block)
// =============================================================================

/**
 * "3 units × $10.00" and the monthly total, the same on the saved-card block and the card form so the
 * price picture survives a fallback. On narrow screens the two stack, each kept on one line.
 */
function PlanTotals({ bundle, units }: { bundle: BundleTemplate; units: number }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
      <span className="whitespace-nowrap text-sm text-muted-foreground">
        {units} unit{units === 1 ? '' : 's'} × {formatUsd(bundle.unitPriceCents, { alwaysDecimals: true })}
      </span>
      <span className="whitespace-nowrap text-sm font-bold text-foreground" data-testid="checkout-monthly-total">
        {formatUsd(units * bundle.unitPriceCents, { alwaysDecimals: true })} / month (+ tax)
      </span>
    </div>
  );
}

function CheckoutSummary({
  bundle,
  units,
  phase,
}: {
  bundle: BundleTemplate;
  units: number;
  phase: 'address' | 'payment';
}) {
  return (
    <>
      <h2 className="text-lg font-semibold text-foreground m-0 mb-3">
        Subscribe to {bundle.name}
      </h2>

      <div className="p-4 bg-accent/50 border border-border rounded-lg mb-4">
        <PlanTotals bundle={bundle} units={units} />
        {phase === 'payment' && (
          <p className="text-xs text-muted-foreground mt-2 m-0">
            Your card will be saved for automatic billing. You will be charged based on your unit count.
          </p>
        )}
      </div>
    </>
  );
}

// =============================================================================
// SAVED CARD CHECKOUT (BR-112): one click with the card already on file
// =============================================================================

function SavedCardCheckout({
  bundle,
  card,
  units,
  busy,
  error,
  onSubscribe,
  onUseDifferentCard,
  onCancel,
}: {
  bundle: BundleTemplate;
  card: DefaultPaymentMethod;
  units: number;
  busy: boolean;
  error: string | null;
  onSubscribe: () => void;
  onUseDifferentCard: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-4" data-testid="saved-card-checkout">
      <h2 className="text-lg font-semibold text-foreground m-0">Subscribe to {bundle.name}</h2>

      <div className="p-4 bg-accent/50 border border-border rounded-lg">
        <PlanTotals bundle={bundle} units={units} />
      </div>

      <ChargeCardLine
        testIdPrefix="saved-card"
        card={card}
        charging={busy}
        changeCardTestId="use-different-card"
        onChangeCard={onUseDifferentCard}
        leadIn="Pay with"
      />

      {error && (
        <PaymentAlert tone="destructive" testId="saved-card-error">
          {error}
        </PaymentAlert>
      )}

      <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-end">
        <Button
          type="button"
          variant="outline"
          className="w-full md:w-auto min-h-[44px]"
          disabled={busy}
          onClick={onCancel}
          data-testid="saved-card-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="w-full md:w-auto min-h-[44px]"
          disabled={busy}
          onClick={onSubscribe}
          data-testid="saved-card-subscribe"
        >
          {busy ? (
            <>
              <Loader2 className="animate-spin" aria-hidden="true" /> Subscribing…
            </>
          ) : (
            `Subscribe using ${formatCardLabel(card)}`
          )}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// CHECKOUT PAGE
// =============================================================================

/**
 * Checkout Page Component
 *
 * Two-phase SetupIntent checkout flow, rendered by CardSetupFlow:
 *   Phase 1: Address form → POST /subscribe → gets SetupIntent secret.
 *   Phase 2: PaymentElement + stripe.confirmSetup() → POST /subscribe/confirm → success page.
 */
export function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bundleId = searchParams.get('bundleId');
  // E2E-only opt-in — see the AddressElement options comment in CardSetupFlow
  // for why this can't be inferred from import.meta.env.PROD or the hostname.
  const disableAddressAutocomplete = searchParams.get('disableAddressAutocomplete') === '1';

  const { data: bundlesData, isLoading: isLoadingBundles, isError: isBundlesError } = useBillingBundles();
  const { data: usageData, isLoading: isLoadingUsage, isError: isUsageError } = useBillingUsage();
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useBillingSubscription();
  // Subscribe + confirm are fresh-session-gated: re-authenticate before the card form, not after submit.
  const sessionFresh = useFreshSession();
  const subscribeMutation = useSubscribeMutation();
  const confirmMutation = useConfirmSubscriptionMutation();

  const [selectedBundle, setSelectedBundle] = useState<BundleTemplate | null>(null);
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minimumUnitRequired, setMinimumUnitRequired] = useState(false);
  // Saved-card branch: `savedCardBusy` locks the button while the subscribe, authentication and confirm run.
  const [useDifferentCard, setUseDifferentCard] = useState(false);
  const [fallbackNote, setFallbackNote] = useState<FallbackNote | null>(null);
  const [savedCardBusy, setSavedCardBusy] = useState(false);
  const [savedCardError, setSavedCardError] = useState<string | null>(null);

  /**
   * Called after the user confirms their billing address.
   * Calls Phase 1 subscribe() and hands the SetupIntent client secret to CardSetupFlow,
   * which moves to Phase 2. Failures replace the form with the page-level error / prompt.
   */
  const initializeCheckout = async (
    bundleTemplateId: string,
    address: BillingAddress,
  ): Promise<{ clientSecret: string }> => {
    try {
      setError(null);
      const result = await subscribeMutation.mutateAsync({
        bundleTemplateId,
        billingAddress: address,
      });

      setPendingSubscriptionId(result.subscriptionId);
      return { clientSecret: result.setupIntentClientSecret };
    } catch (err) {
      // BR-85: 422 MINIMUM_UNIT_REQUIRED — show prompt, suppress checkout form
      if (err instanceof ApiClientError && err.status === 422) {
        setMinimumUnitRequired(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to initialize checkout');
      }
      throw err;
    }
  };

  /**
   * Posts to /subscribe/confirm to emit the CREATE_SUBSCRIPTION command, then leaves for the success
   * page, which waits for the plan to activate (BR-86). Throws on failure.
   */
  const confirmAndFinish = async (setupIntentId: string, subscriptionId: string) => {
    const { before } = await confirmMutation.mutateAsync({ setupIntentId, subscriptionId });
    navigate('/settings/billing/success', { state: { before } });
  };

  /** Called after stripe.confirmSetup() succeeds client-side (card form). */
  const handlePaymentConfirmed = async (setupIntentId: string) => {
    if (!pendingSubscriptionId) {
      setError('Missing subscription ID — please restart checkout');
      return;
    }

    try {
      await confirmAndFinish(setupIntentId, pendingSubscriptionId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm subscription');
    }
  };

  /**
   * Subscribe with the card on file: the server confirms the SetupIntent with it. A bank that wants
   * authentication gets it on this screen; anything that stops the saved card hands over to the card form.
   */
  const subscribeWithSavedCard = async (bundleTemplateId: string) => {
    setSavedCardBusy(true);
    setSavedCardError(null);
    try {
      const result = await subscribeMutation
        .mutateAsync({ bundleTemplateId, useSavedCard: true })
        .catch((err: unknown) => {
          if (err instanceof ApiClientError && err.status === 409) {
            throw new SavedCardFallback(
              err.serverCode === 'BILLING_ADDRESS_REQUIRED'
                ? FALLBACK_NOTES.BILLING_ADDRESS_REQUIRED
                : FALLBACK_NOTES.PAYMENT_METHOD_REQUIRED,
            );
          }
          throw err;
        });
      if (!result.savedCard) throw new SavedCardFallback(FALLBACK_NOTES.PAYMENT_METHOD_REQUIRED);

      let setupIntentId = result.savedCard.setupIntentId;
      if (result.savedCard.status === 'requires_action') {
        // Stripe.js blocked or failed to load, or the authentication call itself rejecting, is not the bank
        // declining: hand over to the card form with a note that names the right cause.
        const stripe = await getStripe().catch(() => null);
        if (!stripe) throw new SavedCardFallback(FALLBACK_NOTES.AUTHENTICATION_UNAVAILABLE);
        const outcome = await stripe
          .handleNextAction({ clientSecret: result.setupIntentClientSecret })
          .catch(() => null);
        if (!outcome) throw new SavedCardFallback(FALLBACK_NOTES.AUTHENTICATION_UNAVAILABLE);
        if (outcome.setupIntent?.status !== 'succeeded') {
          throw new SavedCardFallback(FALLBACK_NOTES.AUTHENTICATION_FAILED);
        }
        setupIntentId = outcome.setupIntent.id;
      }
      await confirmAndFinish(setupIntentId, result.subscriptionId);
    } catch (err) {
      if (err instanceof SavedCardFallback) {
        setFallbackNote(err.note);
        setUseDifferentCard(true);
      } else if (err instanceof ApiClientError && err.status === 422) {
        // BR-85: the unit that made the plan purchasable is gone.
        setMinimumUnitRequired(true);
      } else {
        setSavedCardError(err instanceof Error ? err.message : 'Failed to subscribe with your saved card');
      }
      setSavedCardBusy(false);
    }
  };

  useEffect(() => {
    if (!bundleId) {
      navigate('/settings/billing');
      return;
    }

    if (bundlesData) {
      // Public bundles are in the list; private bundles are passed via router state
      // (e.g. after coupon validation on the billing page).
      const stateBundle = (location.state as { bundle?: BundleTemplate } | null)?.bundle;
      const bundle = bundlesData.bundles.find((b) => b.id === bundleId) ?? stateBundle;

      if (bundle) {
        setSelectedBundle(bundle);
      } else {
        setError('Bundle not found');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleId, bundlesData, navigate]);

  // If Stripe publishable key is not configured, fail fast with a clear message
  // rather than silently rendering a broken payment form.
  if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          <div className="max-w-lg mx-auto">
            <div className="bg-card rounded-lg border border-border p-6">
              <h1 className="text-2xl font-semibold text-destructive m-0 mb-4">
                Payment Not Configured
              </h1>
              <p className="text-base text-foreground mb-5">
                Stripe is not configured for this environment. Please set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> and restart the dev server.
              </p>
              <Button onClick={() => navigate('/settings/billing')} className="min-h-[44px]">
                Return to Billing
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleCancel = () => {
    navigate('/settings/billing');
  };

  // Surface permanent API failures from either data-fetching hook
  const queryError = isBundlesError
    ? 'Failed to load subscription plans. Please refresh and try again.'
    : isUsageError
      ? 'Failed to load your usage data. Please refresh and try again.'
      : null;

  if (isLoadingBundles || isLoadingUsage || isLoadingSubscription || !sessionFresh) {
    return (
      <div className="max-w-4xl mx-auto pb-6" data-testid="checkout-page">
        <div className="px-[var(--page-padding-x)]">
          <div className="max-w-lg mx-auto">
            <div
              className="bg-card rounded-lg border border-border p-6 space-y-4"
              data-testid="checkout-loading"
            >
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (queryError || error || !selectedBundle) {
    return (
      <div className="max-w-4xl mx-auto pb-6" data-testid="checkout-page">
        <div className="px-[var(--page-padding-x)]">
          <div className="max-w-lg mx-auto">
            <div className="bg-card rounded-lg border border-border p-6">
              <h1
                className="text-2xl font-semibold text-destructive m-0 mb-4"
                data-testid="checkout-error"
              >
                Checkout Error
              </h1>
              <p className="text-base text-foreground mb-5">
                {queryError || error || 'Invalid checkout session'}
              </p>
              <Button onClick={handleCancel} className="min-h-[44px]">
                Return to Billing
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // BR-85: Pre-flight — paid bundle + zero active units → block checkout immediately.
  // Also shown if subscribe() returned 422 MINIMUM_UNIT_REQUIRED (e.g. race condition
  // where a unit was deleted between page load and form submission).
  const isPaidBundle = selectedBundle.unitPriceCents > 0;
  const hasNoUnits = usageData !== undefined && usageData.billableUnits < 1;
  const showMinimumUnitRequired = minimumUnitRequired || (isPaidBundle && hasNoUnits);

  if (showMinimumUnitRequired) {
    return (
      <div className="max-w-4xl mx-auto pb-6" data-testid="checkout-page">
        <div className="px-[var(--page-padding-x)]">
          <div className="max-w-lg mx-auto">
            <div className="bg-card rounded-lg border border-border p-6">
              <h1 className="text-2xl font-semibold text-foreground m-0 mb-4">
                Subscription Setup
              </h1>
              <div
                className="text-center"
                data-testid="minimum-unit-required-prompt"
                role="alert"
                aria-live="assertive"
              >
                <div className="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl" aria-hidden="true">🏠</span>
                </div>
                <h2 className="text-xl font-semibold text-foreground m-0 mb-3">
                  Add a Property First
                </h2>
                <p className="text-sm text-muted-foreground mb-2">
                  To subscribe to the <strong>{selectedBundle.name}</strong> plan, you need at
                  least one active property unit in your account.
                </p>
                <p className="text-sm text-muted-foreground mb-5">
                  Add a property and unit, then return to complete your subscription.
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate('/properties')}
                    className="min-h-[44px]"
                    data-testid="go-to-properties-btn"
                  >
                    Add a Property
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="min-h-[44px]"
                    data-testid="minimum-unit-cancel-button"
                  >
                    Return to Billing
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const savedCard = subscriptionData?.defaultPaymentMethod ?? null;
  const units = usageData?.billableUnits ?? 0;

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="checkout-page">
      <div className="px-[var(--page-padding-x)]">
        <div className="max-w-lg mx-auto">
          <div className="bg-card rounded-lg border border-border p-6">
            <h1 className="text-2xl font-semibold text-foreground m-0 mb-6">
              Complete Your Subscription
            </h1>

            {savedCard && !useDifferentCard ? (
              <SavedCardCheckout
                bundle={selectedBundle}
                card={savedCard}
                units={units}
                busy={savedCardBusy}
                error={savedCardError}
                onSubscribe={() => void subscribeWithSavedCard(selectedBundle.id)}
                onUseDifferentCard={() => setUseDifferentCard(true)}
                onCancel={handleCancel}
              />
            ) : (
              <>
                {savedCard && !fallbackNote && (
                  <Button
                    type="button"
                    variant="link"
                    className="mb-2 min-h-[44px] px-0"
                    data-testid="use-saved-card"
                    onClick={() => setUseDifferentCard(false)}
                  >
                    Use {formatCardLabel(savedCard)} instead
                  </Button>
                )}
                {fallbackNote && (
                  <div className="mb-4">
                    <PaymentAlert tone="warning" testId="saved-card-fallback-note">
                      {fallbackNote}
                    </PaymentAlert>
                  </div>
                )}
                {/* Phase 1 (address -> SetupIntent) and Phase 2 (card -> confirmSetup) */}
                <CardSetupFlow
                  requestSetupIntent={(address) => initializeCheckout(selectedBundle.id, address)}
                  onConfirmed={handlePaymentConfirmed}
                  onCancel={handleCancel}
                  summary={(phase) => <CheckoutSummary bundle={selectedBundle} units={units} phase={phase} />}
                  submitLabel="Subscribe"
                  disableAddressAutocomplete={disableAddressAutocomplete}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
